import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext, FunctionElement } from '../../element.model';
import { isFunctionElement } from '../../element.model';
import { findRoots } from '../../../analytics';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import type { InflectionParams } from './inflection.model';

/**
 * Creates inflection points for functions.
 * Inflection points are where the second derivative changes sign (concavity changes).
 * Uses numerical root-finding on the second derivative to find where f''(x) = 0.
 */
export class InflectionCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'inflection';

  private readonly maxInflectionPoints = 10;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as InflectionParams | undefined;
    if (!params?.of) {
      throw new Error('Inflection feature requires params.of to specify the parent function');
    }
    const parentElement = context.elements.get(params.of);
    if (!parentElement || !isFunctionElement(parentElement)) {
      throw new Error(`Inflection feature requires a function element. Got: ${parentElement?.type ?? 'undefined'}`);
    }
    const parentFn = parentElement as FunctionElement;
    const objects: GeometryElement[] = [];

    const style = resolveFeatureStyle('point', this.featureType, context, display, []);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display, []);

    for (let i = 0; i < this.maxInflectionPoints; i++) {
      const result = elementFactory.point({
        coords: [
          () => {
            const bbox = board.getBoundingBox();
            const secondDerivative = parentFn.getDerivative(2);
            const inflectionXValues = findRoots(secondDerivative, bbox[0], bbox[2], {
              maxResults: this.maxInflectionPoints,
            });
            return i < inflectionXValues.length ? inflectionXValues[i] : NaN;
          },
          () => {
            const bbox = board.getBoundingBox();
            const secondDerivative = parentFn.getDerivative(2);
            const inflectionXValues = findRoots(secondDerivative, bbox[0], bbox[2], {
              maxResults: this.maxInflectionPoints,
            });
            if (i < inflectionXValues.length) {
              const y = parentFn.evaluate(inflectionXValues[i]);
              return isFinite(y) ? y : NaN;
            }
            return NaN;
          },
        ],
        name: 'inflection',
        style: { ...style, visible },
      });
      objects.push(result.point);
      if (result.label) {
        objects.push(result.label);
      }
    }

    return {
      id,
      type: 'inflection',
      objects,
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
